import { createGoogleDriveProvider } from "./providers/googleDrive";
import { createMockProvider } from "./providers/mock";

import type { CloudProviderId, CloudStorageProvider } from "./types";

const _cache: Partial<Record<CloudProviderId, CloudStorageProvider>> = {};

const mockEnabled = (): boolean => import.meta.env.VITE_APP_CLOUD_MOCK === "1";

/** Returns the configured provider instance for an id. */
export function getProvider(id: CloudProviderId): CloudStorageProvider | null {
  const cached = _cache[id];
  if (cached) {
    return cached;
  }
  switch (id) {
    case "googleDrive": {
      if (mockEnabled()) {
        const next = createMockProvider(id);
        _cache[id] = next;
        return next;
      }
      const clientId = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID;
      const authUrl = import.meta.env.VITE_APP_GOOGLE_AUTH_URL;
      if (!clientId || !authUrl) {
        return null;
      }
      const next = createGoogleDriveProvider(clientId, authUrl);
      _cache[id] = next;
      return next;
    }
    case "dropbox":
    case "oneDrive":
    case "pCloud":
    case "nextcloud": {
      // Phases 6–8 will swap these in. Mock stays for dev QA.
      if (!mockEnabled()) {
        return null;
      }
      const next = createMockProvider(id);
      _cache[id] = next;
      return next;
    }
  }
  return null;
}

/**
 * Returns false for providers whose runtime config (env var, SDK) is not
 * available. UIs use this to hide disabled entries from the provider
 * switcher.
 */
export function providerIsAvailable(id: CloudProviderId): boolean {
  switch (id) {
    case "googleDrive":
      return (
        mockEnabled() ||
        (!!import.meta.env.VITE_APP_GOOGLE_CLIENT_ID &&
          !!import.meta.env.VITE_APP_GOOGLE_AUTH_URL)
      );
    case "dropbox":
    case "oneDrive":
    case "pCloud":
    case "nextcloud":
      return mockEnabled();
  }
  return false;
}

/** Lists the cloud provider ids available in this runtime. */
export function listProviders(): CloudProviderId[] {
  // Phase 3: only "googleDrive" is user-visible.
  return (["googleDrive"] as CloudProviderId[]).filter(providerIsAvailable);
}
